/**
 * Phase 2 — sentiment fusion table (29 Aug 2026).
 *
 * Sits IN FRONT of `avatar:sentiment` dispatch. Same event name, no new
 * wire, no translator / MOOD_STATES change. Precedence for one turn:
 *
 *   text > prosody > reflex
 *
 * Reflex never wins a turn that already has text or prosody. It is the
 * fallback face when nothing deliberative arrived. Per-source cooldowns
 * stop one producer thrashing `handleSentiment`.
 *
 * Not a frozen contract. Default ON as a gate (it does not attach
 * listeners). Flag-off reflex still never reaches this table.
 */

export type SentimentFusionSource = 'text' | 'prosody' | 'reflex';

export interface SentimentFusionConfig {
  /** Events inside this window after a turn mark share a turn id. */
  turnWindowMs?: number;
  /** Per-source minimum interval between accepted dispatches. */
  cooldownMs?: Partial<Record<SentimentFusionSource, number>>;
}

export interface SentimentFusionDecision {
  accept: boolean;
  reason:
    | 'accepted'
    | 'cooldown'
    | 'same-turn-blocked'
    | 'outranked';
  source: SentimentFusionSource;
}

export interface SentimentDetail {
  label: string;
  score: number;
  source?: string;
  [key: string]: unknown;
}

const RANK: Record<SentimentFusionSource, number> = {
  text: 3,
  prosody: 2,
  reflex: 1,
};

const DEFAULT_TURN_WINDOW_MS = 4000;

const DEFAULT_COOLDOWN_MS: Record<SentimentFusionSource, number> = {
  text: 1500,
  prosody: 1500,
  // Reflex already has its own mood cooldown in the loop.
  reflex: 0,
};

export function inferSentimentSource(detail: SentimentDetail | undefined): SentimentFusionSource {
  const raw = typeof detail?.source === 'string' ? detail.source.toLowerCase() : '';
  if (raw === 'reflex') return 'reflex';
  if (raw === 'prosody' || raw === 'voice' || raw === 'voice-prosody') return 'prosody';
  return 'text';
}

export interface SentimentFusion {
  /** Call on transcript-final / new user turn so reflex becomes fallback again. */
  markTurnBoundary(at?: number): void;
  consider(source: SentimentFusionSource, at?: number): SentimentFusionDecision;
  /**
   * Gate + dispatch `avatar:sentiment` (same public event). Returns true
   * when the event actually fired.
   */
  dispatch(target: EventTarget, detail: SentimentDetail, source?: SentimentFusionSource): boolean;
  reset(): void;
}

export function createSentimentFusion(config?: SentimentFusionConfig): SentimentFusion {
  const turnWindowMs = config?.turnWindowMs ?? DEFAULT_TURN_WINDOW_MS;
  const cooldownMs: Record<SentimentFusionSource, number> = {
    ...DEFAULT_COOLDOWN_MS,
    ...(config?.cooldownMs ?? {}),
  };

  let turnStartedAt = -Infinity;
  let highestThisTurn: SentimentFusionSource | null = null;
  const lastAcceptedAt: Record<SentimentFusionSource, number> = {
    text: -Infinity,
    prosody: -Infinity,
    reflex: -Infinity,
  };

  const ensureTurn = (at: number): void => {
    if (at - turnStartedAt > turnWindowMs) {
      turnStartedAt = at;
      highestThisTurn = null;
    }
  };

  const consider = (source: SentimentFusionSource, at = Date.now()): SentimentFusionDecision => {
    ensureTurn(at);
    const cooldown = cooldownMs[source] ?? 0;
    if (cooldown > 0 && at - lastAcceptedAt[source] < cooldown) {
      return { accept: false, reason: 'cooldown', source };
    }

    if (highestThisTurn && RANK[highestThisTurn] > RANK[source]) {
      // Reflex never wins a turn that already has text or prosody.
      return {
        accept: false,
        reason: source === 'reflex' ? 'same-turn-blocked' : 'outranked',
        source,
      };
    }

    lastAcceptedAt[source] = at;
    if (!highestThisTurn || RANK[source] >= RANK[highestThisTurn]) {
      highestThisTurn = source;
    }
    return { accept: true, reason: 'accepted', source };
  };

  return {
    markTurnBoundary(at = Date.now()): void {
      turnStartedAt = at;
      highestThisTurn = null;
    },
    consider,
    dispatch(target, detail, source): boolean {
      const resolved = source ?? inferSentimentSource(detail);
      const decision = consider(resolved);
      if (!decision.accept) return false;
      target.dispatchEvent(
        new CustomEvent('avatar:sentiment', {
          detail: { ...detail, source: resolved },
        })
      );
      return true;
    },
    reset(): void {
      turnStartedAt = -Infinity;
      highestThisTurn = null;
      lastAcceptedAt.text = -Infinity;
      lastAcceptedAt.prosody = -Infinity;
      lastAcceptedAt.reflex = -Infinity;
    },
  };
}

let runtimeFusion = createSentimentFusion();
const fusionByTarget = new WeakMap<EventTarget, SentimentFusion>();

export function configureSentimentFusion(config?: SentimentFusionConfig): void {
  runtimeFusion = createSentimentFusion(config);
}

export function getSentimentFusion(): SentimentFusion {
  return runtimeFusion;
}

/** One table per EventTarget so tests / surfaces do not leak turns. */
export function fusionFor(target: EventTarget): SentimentFusion {
  if (typeof window !== 'undefined' && target === window) return runtimeFusion;
  let existing = fusionByTarget.get(target);
  if (!existing) {
    existing = createSentimentFusion();
    fusionByTarget.set(target, existing);
  }
  return existing;
}

export function resetSentimentFusion(): void {
  runtimeFusion.reset();
}
